import { useMemo, useState } from 'react';

import {
  TestMsgModals,
  useSendTestMsgContext,
} from 'modules/templateEditor/context/SendTestMsgContext';
import { useDisplayTestMessageStatus } from 'modules/templateEditor/hooks/useDisplayTestMessageStatus';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, ModalFooter, ModalHeader } from 'ui/modal';
import { BasicFilterSelect } from 'ui/selects';
import { Body2 } from 'ui/theme';
import { displayContent, sendProofFilter } from 'modules/templateEditor/common';
import { StyledFormField, StyledModal, StyledModalBody } from '../SendTestMsgModals.styled';

export const DisplayTestMsgToInternalListModal = () => {
  const { onDisplayTestToInternalList, setOpenTestMsgModal, internalListOptions } =
    useSendTestMsgContext();
  const { internalListIdDisplaying, internalListDetailsName } = useDisplayTestMessageStatus();
  const [selectedListId, setSelectedListId] = useState(internalListIdDisplaying?.toString() ?? '');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const memoizedSearchedOptions = useMemo(() => {
    return sendProofFilter(searchTerm, internalListOptions);
  }, [internalListOptions, searchTerm]);

  const handleCloseModal = () => {
    setOpenTestMsgModal(TestMsgModals.None);
  };

  const handleSubmit = (removing = false) => {
    onDisplayTestToInternalList(Number(selectedListId), removing);
    handleCloseModal();
  };

  return (
    <StyledModal
      data-test="displayTestMsgToInternalListModal"
      id="displayTestMsgToInternalListModal"
      isModalOpen={true}
      onClose={handleCloseModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>
        {displayContent.strDisplayTestToInternalListTitle}
      </ModalHeader>
      <StyledModalBody>
        <Body2>{displayContent.strDisplayTestToInternalListSubtitle}</Body2>
        <StyledFormField labelText="Internal list name">
          <BasicFilterSelect
            defaultValues={[internalListDetailsName]}
            id="select-display-test-msg-recipient-list"
            inputContent={{ filterPlaceholder: displayContent.placeholderSearchList }}
            isVirtualized
            onSelect={([newListId]) => setSelectedListId(newListId)}
            options={memoizedSearchedOptions}
            onSearchChange={(searchedItem) => setSearchTerm(searchedItem ?? '')}
            disableInternalFiltering
            isFullWidth
            placeholder="Select an internal list"
            isDisabled={!!internalListIdDisplaying}
            data-test="displayTestMsgListSelect"
          />
        </StyledFormField>
        {/* Hiding until proof system for eligibility is available with end dates */}
        {/* <StyledFormField labelText="Display stop date">
          <DatePickerWithCollapsibleCalendar date={moment(nextDay)} />
          <TypeaheadTimePicker time={moment(nextDay).format('hh:mm A') as TimeIn12HourClock} />
        </StyledFormField> */}
      </StyledModalBody>
      <ModalFooter hasCancel={false}>
        <Button
          variant={BUTTON_VARIANTS.GHOST}
          onClick={handleCloseModal}
          size={BUTTON_SIZES.LARGE}
        >
          {displayContent.btnCancel}
        </Button>
        {internalListIdDisplaying ? (
          <Button
            data-test="displayTestMsgStopBtn"
            onClick={() => handleSubmit(true)}
            variant={BUTTON_VARIANTS.SECONDARYDESTRUCTIVE}
            size={BUTTON_SIZES.LARGE}
          >
            {displayContent.btnClearTestMessage}
          </Button>
        ) : (
          <Button
            data-test="displayTestMsgStartBtn"
            isDisabled={!selectedListId}
            onClick={() => handleSubmit()}
            variant={BUTTON_VARIANTS.PRIMARY}
            size={BUTTON_SIZES.LARGE}
          >
            {displayContent.btnStartTestMessage}
          </Button>
        )}
      </ModalFooter>
    </StyledModal>
  );
};
